import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Boxes, Building2, ChevronLeft, ChevronRight, GraduationCap, Layers, ShieldCheck, Sparkles, Users, Workflow } from 'lucide-react';
import { SiteLayout, useDocumentTitle, CONTACT_EMAIL } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Btn, CardSkeletons, EmptyState, ErrorState } from '../components/platform/ui';
import ProductCard from '../components/platform/ProductCard';
import { CourseCard } from './CabinetPage';
import RequestForm from '../components/platform/RequestForm';
import useAsync from '../hooks/useAsync';
import { platformApi } from '../services/platformApi';
import { useAuth } from '../contexts/AuthContext';
import { LLink } from '../lib/lang';

const WHY_ICONS = [Sparkles, Workflow, Layers, ShieldCheck];

const VideoCarousel = () => {
  const { t } = useTranslation();
  const items = t('videoItems', { returnObjects: true });
  const [index, setIndex] = useState(0);
  if (!Array.isArray(items) || items.length === 0) return null;
  const go = (delta) => setIndex((i) => (i + delta + items.length) % items.length);
  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${index * 100}%)` }}>
          {items.map((item, i) => (
            <div key={i} className="w-full flex-shrink-0 px-2 sm:px-4">
              <div className="vintage-border bg-[#F5F1E8] p-4 sm:p-8">
                <div className="aspect-video bg-black mb-6 relative">
                  <video src={item.videoUrl} className="w-full h-full" title={item.title} controls preload="metadata">
                    {t('pl.home.videoUnsupported')}
                  </video>
                </div>
                <h3 className="text-2xl font-bold mb-3 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title}</h3>
                <p className="text-lg text-gray-700 text-center">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {items.length > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label={t('pl.home.videoPrev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 sm:-translate-x-4 bg-[#F5F1E8] border-2 border-black p-3 hover:bg-black hover:text-white transition-colors">
            <ChevronLeft size={24} />
          </button>
          <button onClick={() => go(1)} aria-label={t('pl.home.videoNext')}
            className="absolute right-0 top-1/2 -translate-y-1/2 sm:translate-x-4 bg-[#F5F1E8] border-2 border-black p-3 hover:bg-black hover:text-white transition-colors">
            <ChevronRight size={24} />
          </button>
          <div className="flex justify-center mt-8 space-x-2">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)} aria-label={t('pl.home.videoGo', { n: i + 1 })}
                className={`w-3 h-3 rounded-full transition-colors ${i === index ? 'bg-[#C41E3A]' : 'bg-gray-400'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Team = () => {
  const { t } = useTranslation();
  const members = t('team.members', { returnObjects: true });
  if (!Array.isArray(members) || members.length === 0) return null;
  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
      {members.map((m, i) => (
        <div key={i} className="vintage-border bg-[#F5F1E8] p-6 text-center flex flex-col h-full">
          <div className="w-full aspect-square mx-auto mb-6 bg-[#E8E3D5] border-2 border-black flex items-center justify-center overflow-hidden">
            {m.photo ? (
              <img src={m.photo} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#E8E3D5] to-[#D6CFBC] flex items-center justify-center">
                <Users size={64} className="text-[#C41E3A] opacity-60" />
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>{m.name}</h3>
            <p className="text-lg text-[#C41E3A] font-semibold mb-2">{m.role}</p>
            <p className="text-gray-700">{m.specialty}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('pl.home.heroTitle'));
  const { user } = useAuth();
  const products = useAsync(platformApi.products, [user?.id, i18n.language]);
  const courses = useAsync(platformApi.courses, [user?.id, i18n.language]);
  const petrovichFirst = !!user?.is_generated_account;
  const why = t('pl.home.why', { returnObjects: true });
  const solutions = t('pl.home.solutions', { returnObjects: true });
  const steps = t('pl.home.platformSteps', { returnObjects: true });

  return (
    <SiteLayout>
      {/* 1. Hero: текст слева, логотип справа, затемнённый фон */}
      <section className="relative border-b-2 border-black bg-gradient-to-br from-[#4a4338] via-[#37312a] to-[#26221d] text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        </div>
        <Container className="relative py-14 sm:py-20 grid lg:grid-cols-[1.25fr,1fr] gap-10 items-center">
          <div>
            <p className="uppercase tracking-widest text-sm text-[#ff8a9b] font-bold mb-4">{t('pl.home.kicker')}</p>
            <Heading level={1} className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6 text-white">{t('pl.home.heroTitle')}</Heading>
            <p className="text-lg sm:text-xl text-white/85 max-w-2xl mb-10">{t('pl.home.heroText')}</p>
            <div className="flex flex-wrap gap-4">
              {petrovichFirst ? (
                <>
                  <Btn to="/metrica" className="text-lg px-6 py-3">{t('pl.home.ctaPetrovich')}<ArrowRight size={18} /></Btn>
                  <Btn to="/klkt-segment" variant="secondary" className="text-lg px-6 py-3">{t('pl.home.ctaSegmentation')}</Btn>
                </>
              ) : (
                <>
                  <Btn to="/catalog" className="text-lg px-6 py-3">{t('pl.home.ctaExplore')}<ArrowRight size={18} /></Btn>
                  <Btn to="/catalog" variant="secondary" className="text-lg px-6 py-3">{t('pl.home.ctaProducts')}</Btn>
                </>
              )}
              <Btn to="/products/clinic-information-system#request" variant="secondary" className="text-lg px-6 py-3">{t('pl.home.ctaClinic')}</Btn>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <img src="/Engraved Classical Bust with TJB Typography.png" alt={t('pl.home.logoAlt')}
              className="w-full max-w-[220px] sm:max-w-xs lg:max-w-sm object-contain border-0 border-white/30 shadow-2xl bg-white/0" />
          </div>
        </Container>
      </section>

      {/* 2. Каталог */}
      <section id="catalog" className="py-16">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <Heading level={2} className="text-3xl sm:text-4xl">{t('pl.home.catalogTitle')}</Heading>
            <LLink to="/catalog" className="underline">{t('pl.home.catalogAll')}</LLink>
          </div>
          {products.loading ? <CardSkeletons /> : products.error ? <ErrorState onRetry={products.reload} /> : products.data.length === 0 ? (
            <EmptyState icon={Boxes} title={t('pl.home.catalogEmptyTitle')} text={t('pl.home.catalogEmptyText')} />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{products.data.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          )}
        </Container>
      </section>

      {/* 3. Чем полезно */}
      <section className="py-16 bg-[#E8E3D5] border-y-2 border-black">
        <Container>
          <Heading level={2} className="text-3xl sm:text-4xl mb-8">{t('pl.home.whyTitle')}</Heading>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(why) && why.map((item, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (<Card key={i}><Icon className="text-[#C41E3A] mb-3" size={28} /><h3 className="font-bold text-lg mb-2">{item.title}</h3><p className="text-gray-700">{item.text}</p></Card>);
            })}
          </div>
        </Container>
      </section>

      {/* Видеообзоры */}
      <section id="videos" className="py-16 tech-lines">
        <Container className="max-w-5xl">
          <Heading level={2} className="text-3xl sm:text-4xl text-center mb-10">
            <span dangerouslySetInnerHTML={{ __html: t('pl.home.videosTitle') }} />
          </Heading>
          <VideoCarousel />
        </Container>
      </section>

      {/* 4. Решения для клиник */}
      <section className="py-16 bg-[#E8E3D5] border-y-2 border-black">
        <Container className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <Building2 className="text-[#C41E3A] mb-3" size={32} />
            <Heading level={2} className="text-3xl sm:text-4xl mb-4">{t('pl.home.clinicsTitle')}</Heading>
            <p className="text-gray-700 mb-5">{t('pl.home.clinicsText')}</p>
            <ul className="space-y-2 mb-6">{Array.isArray(solutions) && solutions.map((s) => <li key={s} className="flex gap-2"><span className="text-[#C41E3A]">—</span>{s}</li>)}</ul>
            <Btn to="/products/clinic-information-system">{t('pl.home.clinicsMore')}</Btn>
          </div>
          <RequestForm productSlug="clinic-information-system" kind="project" title={t('pl.home.clinicsFormTitle')}
            submitLabel={t('pl.product.formClinicSubmit')} messageHint={t('pl.home.clinicsFormHint')} />
        </Container>
      </section>

      {/* 5. Обучение */}
      <section className="py-16">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <Heading level={2} className="text-3xl sm:text-4xl">{t('pl.home.learnTitle')}</Heading>
            <LLink to="/webinars" className="underline">{t('pl.home.learnAll')}</LLink>
          </div>
          {courses.loading ? <CardSkeletons count={2} /> : courses.error ? <ErrorState onRetry={courses.reload} /> : courses.data.length === 0 ? (
            <EmptyState icon={GraduationCap} title={t('pl.home.learnEmptyTitle')} text={t('pl.home.learnEmptyText')} />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{courses.data.slice(0, 3).map((c) => <CourseCard key={c.id} c={c} />)}</div>
          )}
        </Container>
      </section>

      {/* Команда */}
      <section id="team" className="py-16 bg-[#E8E3D5] border-y-2 border-black">
        <Container>
          <div className="text-center mb-10">
            <Heading level={2} className="text-3xl sm:text-4xl mb-3"><span dangerouslySetInnerHTML={{ __html: t('pl.home.teamTitle') }} /></Heading>
            <p className="text-lg text-gray-700">{t('pl.home.teamSubtitle')}</p>
          </div>
          <Team />
        </Container>
      </section>

      {/* 6. Развитие платформы */}
      <section className="py-16">
        <Container className="max-w-4xl">
          <Heading level={2} className="text-3xl sm:text-4xl mb-4">{t('pl.home.platformTitle')}</Heading>
          <p className="text-gray-700 mb-5">{t('pl.home.platformIntro')}</p>
          <ol className="space-y-3">
            {Array.isArray(steps) && steps.map((s, i) => (
              <li key={s} className="flex gap-4 items-center"><span className="w-9 h-9 shrink-0 flex items-center justify-center border-2 border-black font-bold">{i + 1}</span>{s}</li>
            ))}
          </ol>
        </Container>
      </section>

      {/* 7. Финальный призыв */}
      <section className="py-16 bg-black text-white">
        <Container className="text-center">
          <Heading level={2} className="text-3xl sm:text-4xl mb-4">{t('pl.home.finalTitle')}</Heading>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">{t('pl.home.finalText')} <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
          <div className="flex flex-wrap gap-4 justify-center">
            {user ? <Btn to="/cabinet">{t('pl.nav.cabinet')}</Btn> : <Btn to="/register">{t('pl.home.finalRegister')}</Btn>}
            <Btn to="/catalog" variant="secondary" className="!bg-transparent !text-white !border-white">{t('pl.home.finalCatalog')}</Btn>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
};

export default HomePage;
